import i18n from 'i18n/config';
import { CapitalizeEachWord, oneToOneMapping } from 'common/utils';
import { getLocationDisplayName } from 'common/utils/locationDisplayName';
import {
  LOCATION_TYPE_KEYS,
  getLocationTypeLabel,
  isLocationTypeKey
} from 'common/utils/locationTypeKey';
import { getTagTypeDisplayName } from 'common/utils/tagTypeDisplayName';
import { slugify } from 'common/utils/slug';
import { SHORTER_CHART_CONFIG_NAME_MAPPINGS } from 'home/AssetHierarchy/constants/assetHierarchy.constants';

const DIRECTION_KEYS = ['tangential', 'axial', 'radial', 'horizontal', 'vertical'];
// Longer compounds first so oneToOneMapping prefers them over shorter suffixes.
const LOCATION_PHRASE_KEYS = [
  'screwCompressorIdleShaftDriveEnd',
  'screwCompressorIdleShaftFreeEnd',
  'screwCompressorInputShaftFreeEnd',
  'screwCompressorInputShaftDriveEnd',
  'gearboxInputEnd',
  'gearboxOutputEnd',
  'inputShaftFreeEnd',
  'inputShaftDriveEnd',
  'intermediateShaftFreeEnd',
  'intermediateShaftDriveEnd',
  'outputShaftFreeEnd',
  'outputShaftDriveEnd',
  'idleShaftFreeEnd',
  'idleShaftDriveEnd',
  'shaftFreeEnd',
  'shaftDriveEnd',
  'bearingDriveEnd',
  'freeEnd',
  'driveEnd',
  'inputEnd',
  'outputEnd',
  'inputShaft',
  'outputShaft',
  'intermediateShaft',
  'idleShaft',
  'leftSide',
  'rightSide',
];
const LOCATION_PHRASE_DEFAULTS = {
  screwCompressorIdleShaftDriveEnd: 'Screw Compressor Idle Shaft Drive End',
  screwCompressorIdleShaftFreeEnd: 'Screw Compressor Idle Shaft Free End',
  screwCompressorInputShaftFreeEnd: 'Screw Compressor Input Shaft Free End',
  screwCompressorInputShaftDriveEnd: 'Screw Compressor Input Shaft Drive End',
  gearboxInputEnd: 'Gearbox Input End',
  gearboxOutputEnd: 'Gearbox Output End',
  inputShaftFreeEnd: 'Input Shaft Free End',
  inputShaftDriveEnd: 'Input Shaft Drive End',
  intermediateShaftFreeEnd: 'Intermediate Shaft Free End',
  intermediateShaftDriveEnd: 'Intermediate Shaft Drive End',
  outputShaftFreeEnd: 'Output Shaft Free End',
  outputShaftDriveEnd: 'Output Shaft Drive End',
  idleShaftFreeEnd: 'Idle Shaft Free End',
  idleShaftDriveEnd: 'Idle Shaft Drive End',
  shaftFreeEnd: 'Shaft Free End',
  shaftDriveEnd: 'Shaft Drive End',
  bearingDriveEnd: 'Bearing Drive End',
  freeEnd: 'Free End',
  driveEnd: 'Drive End',
  inputEnd: 'Input End',
  outputEnd: 'Output End',
  inputShaft: 'Input Shaft',
  outputShaft: 'Output Shaft',
  intermediateShaft: 'Intermediate Shaft',
  idleShaft: 'Idle Shaft',
  leftSide: 'Left Side',
  rightSide: 'Right Side',
};
const hierarchyMapCache = {};

function capitalizeWord(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function addLocationPhraseMappings(t, map) {
  LOCATION_PHRASE_KEYS.forEach((phraseKey) => {
    const enName = LOCATION_PHRASE_DEFAULTS[phraseKey];
    if (!enName) return;
    map[enName] = t(`assetHierarchy.locationPhrases.${phraseKey}`, { defaultValue: enName });
  });
}

function isStandardLocationTypeName(name) {
  return isLocationTypeKey(slugify(name));
}

function clearHierarchyMapCache() {
  Object.keys(hierarchyMapCache).forEach((key) => {
    delete hierarchyMapCache[key];
  });
}

i18n.on('languageChanged', clearHierarchyMapCache);
i18n.on('loaded', clearHierarchyMapCache);

function buildEnglishToLocalizedMap(t) {
  const enT = i18n.getFixedT('en');
  const map = {};

  LOCATION_TYPE_KEYS.forEach((key) => {
    const enName = enT(`assets.options.locationTypes.${key}`, { defaultValue: '' });
    if (enName) {
      map[enName] = getLocationTypeLabel(t, key, enName);
    }
  });

  DIRECTION_KEYS.forEach((key) => {
    const enName = enT(`common.directions.${key}`, { defaultValue: key });
    const localized = t(`common.directions.${key}`, { defaultValue: enName });
    map[enName] = localized;
    map[capitalizeWord(enName)] = capitalizeWord(localized);
  });

  Object.keys(SHORTER_CHART_CONFIG_NAME_MAPPINGS).forEach((enKey) => {
    if (enKey === 'Vibration') {
      map[enKey] = getTagTypeDisplayName(t, { tagType: 'vibration' }) || enKey;
    } else if (enKey === 'Gearbox') {
      map[enKey] = t('tagTemplates.componentTypes.gearbox', { defaultValue: enKey });
    } else if (!map[enKey]) {
      map[enKey] = getLocationDisplayName(t, { name: enKey });
    }
  });

  for (let id = 1; id <= 16; id += 1) {
    const enName = enT(`tagTemplates.tagTypes.${id}`, { defaultValue: '' });
    if (enName && !map[enName]) {
      map[enName] = getTagTypeDisplayName(t, { tagTypeId: id }) || enName;
    }
  }

  map['Utilization Trend'] = t('assetHierarchy.chartNames.utilizationTrend', {
    defaultValue: 'Utilization Trend',
  });
  map.on = t('assetHierarchy.chartNames.on', { defaultValue: 'on' });

  addLocationPhraseMappings(t, map);

  return map;
}

function buildLocalizedShorterMap(t, englishToLocalized) {
  const map = {};
  Object.entries(SHORTER_CHART_CONFIG_NAME_MAPPINGS).forEach(([enKey, shortEn]) => {
    const localizedLong = englishToLocalized[enKey] || enKey;
    const localizedShort = t(`assetHierarchy.shorterNames.${slugify(enKey)}`, { defaultValue: shortEn });
    map[localizedLong] = localizedShort;
  });
  return map;
}

function getLanguageKey(t) {
  return (t.i18n && t.i18n.language) || i18n.language || 'en';
}

function getCachedHierarchyMaps(t) {
  const lng = getLanguageKey(t);
  if (!hierarchyMapCache[lng]) {
    const englishToLocalized = buildEnglishToLocalizedMap(t);
    hierarchyMapCache[lng] = {
      englishToLocalized,
      shorterMap: buildLocalizedShorterMap(t, englishToLocalized),
    };
  }
  return hierarchyMapCache[lng];
}

/**
 * Localized chart/config label for asset hierarchy tree and related views.
 *
 * @param {function} t - i18next `t`
 * @param {string} name - API config name (English)
 * @param {{ shorten?: boolean }} [options]
 * @returns {string}
 */
export function getHierarchyConfigDisplayName(t, name, { shorten = true } = {}) {
  if (!name) return '';

  if (name === 'Utilization Trend') {
    return t('assetHierarchy.chartNames.utilizationTrend', { defaultValue: name });
  }

  const { englishToLocalized, shorterMap } = getCachedHierarchyMaps(t);
  let result = oneToOneMapping(englishToLocalized, name);

  if (shorten) {
    result = oneToOneMapping(shorterMap, result);
  }

  return result;
}

/**
 * Localized location label for asset hierarchy tree nodes.
 *
 * @param {function} t - i18next `t`
 * @param {string} name - API location name (English)
 * @returns {string}
 */
export function getHierarchyLocationDisplayName(t, name) {
  if (!name) return '';
  if (isStandardLocationTypeName(name)) {
    return getLocationDisplayName(t, { name });
  }
  // Custom names (e.g. "Motor1 Free End") — translate embedded location phrases.
  return getHierarchyConfigDisplayName(t, name, { shorten: false });
}

/**
 * Build a localized tag/chart label from structured tag fields (same order as chart legends).
 *
 * @param {function} t - i18next `t`
 * @param {{ locationDescription?: string, tagType?: string, direction?: string }} parts
 * @param {{ shorten?: boolean }} [options]
 * @returns {string}
 */
export function buildHierarchyTagDisplayName(t, parts, { shorten = true } = {}) {
  const { locationDescription, tagType, direction } = parts || {};
  const segments = [locationDescription, tagType, direction]
    .filter(segment => typeof segment === 'string' && segment.length > 0)
    .map(CapitalizeEachWord);
  if (!segments.length) return '';
  return getHierarchyConfigDisplayName(t, segments.join(' '), { shorten });
}

/**
 * Localized hierarchy node title by node type.
 *
 * @param {function} t - i18next `t`
 * @param {{ node_type?: string, node_name?: string }} node
 * @param {{ shortenConfig?: boolean }} [options]
 * @returns {string}
 */
export function getHierarchyNodeDisplayName(t, node, { shortenConfig = true } = {}) {
  if (!node) return '';

  const { node_type: nodeType, node_name: nodeName } = node;

  if (nodeType === 'machine_charts') {
    return t('machineDashboard.health.sidebar.allCharts');
  }
  if (nodeType === 'data_overview') {
    return t('machineDashboard.health.sidebar.dataOverview');
  }
  if (nodeType === 'location') {
    return getHierarchyLocationDisplayName(t, nodeName);
  }
  if (nodeType === 'config') {
    return getHierarchyConfigDisplayName(t, nodeName, { shorten: shortenConfig });
  }

  return nodeName || '';
}
